/**
 * API Hooks for StockSync Hub Frontend
 * React Query-based hooks with real API integration + mock fallbacks
 */

import { QueryClient, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardApi, productsApi, channelsApi, syncApi, aiDashboardApi } from '../api/client';
import type { DashboardOverview, AIDashboardOverview, BootstrapData, CatalogSearchResponse } from '../api/client';
import type {
  Product,
  UnmappedProduct,
  MissingChannel,
  Channel,
  Agent,
  SyncActivity,
  DashboardStats,
  ApiResponse,
  PaginatedResponse,
} from '../types';


// Query client configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

// ============ Query Keys ============

export const queryKeys = {
  products: ['products'] as const,
  product: (id: string) => ['products', id] as const,
  channels: ['channels'] as const,
  channel: (id: string) => ['channels', id] as const,
  alerts: ['alerts'] as const,
  alertsUnread: ['alerts', 'unread'] as const,
  dashboard: ['dashboard'] as const,
  dashboardStats: ['dashboard', 'stats'] as const,
  dashboardOverview: ['dashboard', 'overview'] as const,
  agents: ['agents'] as const,
  syncActivity: ['sync', 'activity'] as const,
  syncStatus: ['sync', 'status'] as const,
  bootstrap: ['dashboard', 'bootstrap'] as const,
  aiDashboard: ['ai-dashboard'] as const,
  aiDashboardOverview: ['ai-dashboard', 'overview'] as const,
};

// ============ Bootstrap Hook ============

export const useBootstrap = () => {
  return useQuery<BootstrapData>({
    queryKey: queryKeys.bootstrap,
    queryFn: async () => {
      return await dashboardApi.getBootstrap();
    },
    staleTime: 60000, // 1 minute
  });
};

// ============ Dashboard Hooks ============

export const useDashboardOverview = (options?: { enabled?: boolean }) => {
  return useQuery<DashboardOverview>({
    queryKey: queryKeys.dashboardOverview,
    queryFn: async () => {
      return await dashboardApi.getOverview();
    },
    staleTime: 60000, // 1 minute
    enabled: options?.enabled ?? true,
  });
};

export const useDashboardStats = () => {
  return useQuery<DashboardStats>({
    queryKey: queryKeys.dashboardStats,
    queryFn: async () => {
      const stats = await dashboardApi.getStats();
      return {
        totalProducts: stats.totalProducts ?? 0,
        syncedToday: stats.syncedToday ?? 0,
        activeChannels: stats.activeChannels ?? 0,
        alertsCount: stats.pendingAlerts ?? 0,
      };
    },
  });
};

export const useAgents = (options?: { enabled?: boolean }) => {
  return useQuery<Agent[]>({
    queryKey: queryKeys.agents,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      try {
        // GET /dashboard/agents returns real operational data from poller, guardian, sync events, alerts
        const agentData: any[] = await dashboardApi.getAgentStatus() as any;

        // If backend returned an array (new endpoint), map directly
        if (Array.isArray(agentData)) {
          return agentData.map((a: any) => ({
            id: a.id,
            type: a.type as 'watcher' | 'sync' | 'guardian' | 'alert',
            name: a.name,
            status: a.status || 'idle',
            lastActivity: a.lastActivity || null,
            tasksCompleted: a.tasksCompleted || 0,
            icon: a.type === 'watcher' ? String.fromCodePoint(0x25C8) :
                  a.type === 'sync' ? String.fromCodePoint(0x21BB) :
                  a.type === 'guardian' ? String.fromCodePoint(0x2B21) :
                  String.fromCodePoint(0x2691),
            description: a.description,
          }));
        }

        // Fallback for unexpected response shape
        return [
          { id: 'watcher', type: 'watcher' as const, name: 'Stock Watcher', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x25C8) },
          { id: 'sync', type: 'sync' as const, name: 'Sync Engine', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x21BB) },
          { id: 'guardian', type: 'guardian' as const, name: 'Stock Guardian', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x2B21) },
          { id: 'alert', type: 'alert' as const, name: 'Alert Manager', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x2691) },
        ];
      } catch (error) {
        console.error('Failed to fetch agent status:', error);
        return [
          { id: 'watcher', type: 'watcher' as const, name: 'Stock Watcher', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x25C8) },
          { id: 'sync', type: 'sync' as const, name: 'Sync Engine', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x21BB) },
          { id: 'guardian', type: 'guardian' as const, name: 'Stock Guardian', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x2B21) },
          { id: 'alert', type: 'alert' as const, name: 'Alert Manager', status: 'idle' as const, lastActivity: null, tasksCompleted: 0, icon: String.fromCodePoint(0x2691) },
        ];
      }
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

export const useSyncActivity = (limit = 10) => {
  return useQuery<SyncActivity[]>({
    queryKey: ['sync', 'activity', limit],
    queryFn: async (): Promise<SyncActivity[]> => {
      try {
        const events = await dashboardApi.getRecentActivity(limit);
        return (events || []).map((event: any) => ({
          id: event.id,
          type: event.eventType || event.type || 'sync',
          message: event.message || `${event.eventType} - ${event.status}`,
          timestamp: event.createdAt || event.timestamp,
          channelType: event.channelType || event.channel?.type,
          productName: event.productName || event.product?.name,
          oldValue: typeof event.oldValue?.stock === 'number' ? event.oldValue.stock : undefined,
          newValue: typeof event.newValue?.stock === 'number' ? event.newValue.stock : undefined,
        }));
      } catch (error) {
        console.error('Failed to fetch sync activity:', error);
        return [];
      }
    },
    refetchInterval: 300000, // 5-minute fallback; real-time updates come via WebSocket
  });
};

// ============ Products Hooks ============

export const useProducts = (filters?: { channel?: string; search?: string; sort?: string; order?: string }) => {
  return useQuery<Product[]>({
    queryKey: ['products', filters],
    queryFn: async () => {
      try {
        const response = await productsApi.list({
          search: filters?.search,
          limit: 5000,
          sort: filters?.sort,
          order: filters?.order,
          channel: filters?.channel,
        });
        const products = response.data || [];
        return products.map((p: any) => ({
          id: p.id,
          sku: p.sku,
          name: p.name,
          stock: p.currentStock ?? p.stock ?? 0,
          bufferStock: p.bufferStock ?? 0,
          price: p.price ?? p.metadata?.price ?? 0,
          channels: (p.channelMappings || p.channels || []).map((m: any) => ({
            channelId: m.channelId || m.id,
            channelType: m.channelType || m.channel?.type || 'unknown',
            externalId: m.externalId || '',
            stock: m.stock ?? p.currentStock ?? 0,
            price: m.price ?? p.price ?? 0,
            lastSync: m.lastSync || m.createdAt || new Date().toISOString(),
            status: m.status || 'synced',
          })),
          lastSync: p.updatedAt || p.lastSync || p.createdAt,
        }));
      } catch (error) {
        console.error('Failed to fetch products:', error);
        return [];
      }
    },
  });
};

export const useUpdateProductStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, stock }: { productId: string; stock: number }) => {
      return await productsApi.updateStock(productId, stock);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
};

export const useUnmappedProducts = (search?: string) => {
  return useQuery<UnmappedProduct[]>({
    queryKey: ['products', 'unmapped', search ?? ''],
    queryFn: async () => {
      const response = await productsApi.listUnmapped({ search: search || undefined, limit: 5000 });
      const items = (response.data || []) as any[];
      return items.map((p: any) => ({
        id: p.id,
        sku: p.sku,
        name: p.name,
        stock: p.currentStock ?? p.stock ?? 0,
        bufferStock: p.bufferStock ?? 0,
        price: p.price ?? p.metadata?.price ?? 0,
        channels: (p.channelMappings || []).map((m: any) => ({
          channelId: m.channelId,
          channelType: m.channel?.type || 'unknown',
          externalId: m.externalId || '',
          stock: p.currentStock ?? 0,
          price: p.price ?? 0,
          lastSync: m.createdAt || null,
          status: 'synced' as const,
        })),
        lastSync: p.updatedAt || p.createdAt || null,
        missingChannels: (p.missingChannels || []) as MissingChannel[],
      }));
    },
  });
};

export const useAddMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { productId: string; channelId: string; externalId: string; externalSku?: string }) => {
      const { productId, ...data } = args;
      return await productsApi.addMapping(productId, data);
    },
    onSuccess: () => {
      // catches both ['products', filters] and ['products', 'unmapped', search]
      queryClient.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
};

export const useChannelCatalogSearch = (channelId: string | null, search: string) => {
  return useQuery<CatalogSearchResponse>({
    queryKey: ['channel-catalog', channelId, search],
    enabled: !!channelId,
    staleTime: 1000 * 60, // snapshot is server-side cached; avoid refetch churn
    retry: 1, // a 503 (kill switch / cool-off) should surface, not retry 3x
    queryFn: async () => {
      return await channelsApi.searchCatalog(channelId!, search);
    },
  });
};

// ============ Channels Hooks ============

export const useChannels = (options?: { enabled?: boolean }) => {
  return useQuery<Channel[]>({
    queryKey: queryKeys.channels,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      try {
        const channels = await channelsApi.list();
        return (channels || []).map((ch: any) => ({
          id: ch.id,
          type: ch.type,
          name: ch.name,
          status: ch.isActive ? 'connected' : 'disconnected',
          lastSync: ch.lastSyncAt || ch.createdAt,
          productCount: ch.productCount,
        }));
      } catch (error) {
        console.error('Failed to fetch channels:', error);
        return [];
      }
    },
  });
};

export const useConnectChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelType: string) => {
      return await channelsApi.create({ type: channelType as any, name: channelType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels });
    },
  });
};

export const useDisconnectChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      await channelsApi.delete(channelId);
      return { channelId, success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels });
    },
  });
};

export const useSyncChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      return await channelsApi.sync(channelId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels });
      queryClient.invalidateQueries({ queryKey: queryKeys.syncActivity });
    },
  });
};

export const useUpdateChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { credentials?: Record<string, unknown>; name?: string; isActive?: boolean; externalInstanceId?: string } }) => {
      return await channelsApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels });
    },
  });
};

// ============ AI Dashboard Hooks ============

export const useAIDashboardOverview = () => {
  return useQuery<AIDashboardOverview>({
    queryKey: queryKeys.aiDashboardOverview,
    queryFn: () => aiDashboardApi.getOverview(),
    staleTime: 60000,
    refetchInterval: 5 * 60 * 1000,
  });
};

export { dashboardApi, productsApi, channelsApi, syncApi, aiDashboardApi } from '../api/client';
